import { useEffect, useState } from 'react';

import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import DatePicker from 'components/Input/DatePicker/DatePicker';
import Card from 'components/Card/Card';
import ClassDropDown from 'components/Header/components/ClassDropDown/ClassDropDown';
import TimePicker from 'components/Input/TimePicker/TimePicker';
import Button from 'components/Button/Button';
import './TeacherDashboard.scoped.scss';
import Header from 'components/LiveClassComponents/Header/Header';
import {
  getAllStudents,
  liveClassStudentsState,
  resetStudentsList,
} from 'redux/liveClass/students.slices';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import LiveClassLessonsList from 'components/LiveClassComponents/LessonsList/LiveClassLessonsList';
import {
  clearResponse,
  createLiveClass,
  setModelProp,
  teacherCreateLiveClassState,
} from 'redux/liveClass/teacherCreateLiveClass.slices';
import { getUser } from 'redux/authentication/auth.slice';
import useHeaderClasses from 'application/header/useHeaderClasses';

import Groups from './components/groups/Groups';
import Students from './components/students/Index';
import Notification from '../Notifications/Modal';
import SelectClass from '../../../../clean/components/SelectClassAndSubject/SelectClass';
import SelectSubject from '../../../../clean/components/SelectClassAndSubject/SelectSubject';

const TeacherDashboard = ({ history }) => {
  const { t } = useTranslation('live_classes');
  const dispatch = useAppDispatch();
  const { selectedStudents } = useAppSelector(liveClassStudentsState);
  // const activeSubject = useAppSelector(getHeaderActiveClass);
  const user = useAppSelector(getUser);

  const { model, loading, response } = useAppSelector(teacherCreateLiveClassState);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { classes, activeSubject, setActiveSubject } = useHeaderClasses();
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState({
    title: t('common:SelectSubject'), // 'Select subject',
    id: null,
  });
  const [selectionType, setSelectionType] = useState(1);
  const [selectionTypeLabel, setSelectionTypeLabel] = useState(t('students'));
  const [openModal, setOpenModal] = useState(false);
  const [modalContent, setModalContent] = useState(<></>);
  const [className, setClassName] = useState('');

  const handleModal = () => {
    setOpenModal(prevState => !prevState);
  };

  useEffect(() => {
    if (user) {
      dispatch(
        setModelProp({
          value: `${user.title} ${user.first_name} ${user.last_name}`,
          prop: 'teacher_name',
        })
      );
    }
  }, [user]);

  useEffect(() => {
    if (activeSubject) {
      dispatch(
        setModelProp({
          value: `${selectedSubject.title} ${className}`,
          prop: 'name',
        })
      );
    }
  }, [selectedSubject, className]);

  useEffect(() => {
    if (activeSubject) {
      dispatch(
        setModelProp({
          value: activeSubject.id,
          prop: 'lesson_id',
        })
      );
    }
  }, [activeSubject]);

  useEffect(() => {
    switch (loading) {
      case 'pending':
        setModalContent(
          <>
            <h3>{t('Creating class')}</h3>
            <p>{t('Please wait until everyting is ready')}</p>
          </>
        );
        break;
      case 'success':
        if (!response.id) {
          return;
        }
        dispatch(clearResponse());
        history.push(`live/${response.id}`);
        break;
      default: // do nothing
    }
  }, [loading]);

  useEffect(() => {
    if (!selectedClass) {
      return;
    }
    dispatch(getAllStudents(selectedClass.id));
  }, [selectedClass]);

  useEffect(() => {
    dispatch(
      setModelProp({
        prop: 'students',
        value: selectedStudents,
      })
    );
  }, [selectedStudents]);

  const durationSelectOptions: any[] = [];
  useEffect(function () {
    for (let i = 5; i <= 60; i += 5) {
      durationSelectOptions.push(
        <div
          className={`dropdown-element pointer ${i === model.duration && 'selected'}`}
          key={`${model.duration}-${i}`}
          onClick={() => {
            dispatch(
              setModelProp({
                prop: 'duration',
                value: i.toString(),
              })
            );
          }}
        >
          <p>{i} min</p>
        </div>
      );
    }
  });

  useEffect(() => {
    dispatch(resetStudentsList());
  }, []);

  return (
    <>
      <Notification handleModal={handleModal} openModal={openModal}>
        {modalContent}
      </Notification>
      <div className="live-class-container main-input">
        <Card>
          <Card.Header>
            <Header showFullScreen={false} title={model.name}></Header>
          </Card.Header>

          <Card.Body style={{ position: 'relative', padding: 0 }}>
            <div className="body-container p-1">
              <div className="flex gap-1">
                <div className="flex flex-col mb-1 w-full text-bold">
                  <SelectClass handleSelect={setSelectedClass} selectedId={selectedClass?.id} />
                </div>
                <div className="flex flex-col mb-1 w-full text-bold">
                  <SelectSubject
                    handleSelect={setSelectedSubject}
                    parentId={selectedClass?.id}
                    selectedId={selectedSubject.id}
                  />
                </div>
              </div>
              <div className="flex align-center ">
                <div className="flex flex-col mb-1 w-full">
                  <label htmlFor="duration" className="m-r-5">
                    <h4>{t('Class duration:')}</h4>
                  </label>
                  <ClassDropDown
                    value={`${model.duration} min`}
                    styleDropDown={{ left: 'auto', right: '0px' }}
                    icon={''}
                  >
                    <div>{durationSelectOptions}</div>
                  </ClassDropDown>
                </div>
              </div>
              <div className="basic-info">
                <div>
                  <div className=" flex column margin-x-10px">
                    <label htmlFor="className">
                      <h4>{t('Name')}</h4>
                    </label>
                    <input
                      type="text"
                      className="input  border-radius-10px"
                      onChange={e => setClassName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex column">
                  <div className="margin-x-10px">
                    <label htmlFor="date-picker">
                      <h4>{t('Start Date')}</h4>
                    </label>
                    <div className="date-picker">
                      <DatePicker
                        value={model.starting_at}
                        onChange={({ dateStr }) => {
                          dispatch(
                            setModelProp({
                              prop: 'starting_at',
                              value: dateStr,
                            })
                          );
                        }}
                      />
                    </div>
                  </div>
                  <div className="margin-x-10px">
                    <label htmlFor="date-picker">
                      <h4>{t('Start-Time')}</h4>
                    </label>
                    <div className="date-picker">
                      <TimePicker
                        onChange={e => {
                          const data = e.split(':');
                          const starting_at = DateTime.fromISO(model.starting_at)
                            .set({
                              hour: data[0],
                              minute: data[1],
                            })
                            .toString();

                          dispatch(
                            setModelProp({
                              prop: 'starting_at',
                              value: starting_at.toString(),
                            })
                          );
                        }}
                        value={DateTime.fromISO(model.starting_at).toFormat('HH:mm')}
                      />
                    </div>
                  </div>
                </div>
                <div className="description flex column margin-x-10px">
                  <label htmlFor="description">
                    <h4>{t('Description')}</h4>
                  </label>
                  <textarea
                    id="description"
                    className="input border-radius-10px"
                    rows={10}
                    onChange={e =>
                      dispatch(
                        setModelProp({
                          prop: 'description',
                          value: e.target.value,
                        })
                      )
                    }
                  />
                </div>
              </div>
              <div className="divider m-y-3" />
              <div className="type-of-adding-students flex">
                <h4>{t('How do you want to add:')}</h4>
                <ClassDropDown value={selectionTypeLabel.toString()} icon={''}>
                  <div>
                    <div
                      className={`dropdown-element pointer ${selectionType === 1 && 'selected'}`}
                      key={'select-1'}
                      onClick={() => {
                        setSelectionType(1);
                        setSelectionTypeLabel(t('students'));
                      }}
                    >
                      <p>{t('students')}</p>
                    </div>
                    <div
                      className={`dropdown-element pointer ${selectionType === 2 && 'selected'}`}
                      key={'select-2'}
                      onClick={() => {
                        setSelectionType(2);
                        setSelectionTypeLabel(t('Groups'));
                      }}
                    >
                      <p>{t('Groups')}</p>
                    </div>
                  </div>
                </ClassDropDown>
              </div>
              <div className="divider m-y-3" />
              {selectionType === 1 ? <Students /> : ''}
              {selectionType === 2 ? <Groups /> : ''}
              <footer>
                <Button
                  round
                  style={{
                    background: '#5CD79E',
                    color: 'white',
                    fontSize: 14,
                    fontWeight: 600,
                    padding: '.5em 2em',
                  }}
                  onClick={async () => {
                    if (!selectedStudents.length) {
                      setModalContent(
                        <>
                          <h3>{t('No students selected')}</h3>
                          <p>{t('Please select at least one student')}</p>
                        </>
                      );
                      handleModal();
                      return;
                    }

                    dispatch(createLiveClass(model));
                  }}
                >
                  {' '}
                  {t('Start live class')}
                </Button>
              </footer>
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <Header showFullScreen={false} title={t('Active Classes')}></Header>
          </Card.Header>

          <Card.Body style={{ position: 'relative', padding: 0 }}>
            <div className="body-container p-1">
              <LiveClassLessonsList />
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

export default TeacherDashboard;
